import { Controller } from "@hotwired/stimulus"

// Drives the avatar builder entirely client side. Every variant of every slot is
// already in the SVG, so picking one is a display toggle and picking a colour is a
// custom-property write — no round trip, no flicker.
export default class extends Controller {
  static targets = [
    "canvas", "tile", "panel", "pickers", "fade", "swatch", "field", "colourField", "tab",
    "balance", "purchaseItems", "confirmHost", "confirmTemplate", "unlock", "unlockLabel",
  ]

  static values = { balance: Number, spent: Number }

  connect() {
    // Landing here after a purchase: count the balance down and pop the avatar, so
    // spending feels like something happened rather than a page reload.
    if (this.hasSpentValue && this.spentValue > 0) this.#celebrate()

    this.current = Object.fromEntries(
      this.#activeIn(this.tileTargets).map((tile) => [tile.dataset.slot, tile.dataset.variant])
    )
    // Where each slot goes back to when a locked item is set aside.
    this.lastOwned = { ...this.current }

    // Eyebrows are painted by the hair swatch, so their colour is not a choice of
    // its own. Counting it as one leaves a look looking changed when it is not.
    this.derived = new Set(
      this.swatchTargets.flatMap((swatch) => (swatch.dataset.also || "").split(" ").filter(Boolean))
    )

    // Which hats replace hair rather than sit in it — the tiles carry it so this and
    // the sprite cannot drift apart.
    this.covering = new Set(
      this.tileTargets.filter((tile) => tile.dataset.covers).map((tile) => tile.dataset.variant)
    )

    this.history = [this.#snapshot()]
    this.cursor = 0
    this.#refresh()
    // After layout, or the column has no height to measure yet.
    requestAnimationFrame(() => this.fade())
  }

  get canUndo() { return this.cursor > 0 }
  get canRedo() { return this.cursor < this.history.length - 1 }

  undo() { this.#travel(-1) }
  redo() { this.#travel(1) }

  pickGroup(event) {
    const { group } = event.currentTarget.dataset

    this.tabTargets.forEach((tab) => {
      tab.dataset.active = String(tab.dataset.group === group)
      tab.setAttribute("aria-pressed", String(tab.dataset.group === group))
    })

    this.openSlot(group)
  }

  openSlot(group) {
    this.panelTargets.forEach((panel) => {
      panel.style.display = panel.dataset.group === group ? "" : "none"
    })

    // A new tab starts at its own top, not wherever the last one was left.
    if (this.hasPickersTarget) this.pickersTarget.scrollTop = 0
    this.fade()
  }

  fade() {
    if (!this.hasFadeTarget || !this.hasPickersTarget) return

    const { scrollTop, clientHeight, scrollHeight } = this.pickersTarget

    this.fadeTarget.hidden = scrollTop + clientHeight >= scrollHeight - 1
  }

  // Buying asks first rather than spending the moment the price is tapped.
  unlock() {
    const trial = this.#trial()

    if (trial) this.#askToUnlock(trial)
  }

  confirmUnlock() {
    const trial = this.#trial()
    if (!trial) return

    // The purchase posts the whole look, so a save still waiting would only race it
    // and could land last, without the item.
    clearTimeout(this.saveTimer)
    this.saveTimer = null

    this.purchaseItemsTarget.value = `${trial.dataset.slot}:${trial.dataset.variant}`

    const form = this.purchaseItemsTarget.form
    const fields = [...this.fieldTargets, ...this.colourFieldTargets]

    fields.forEach((field) => {
      const copy = field.cloneNode()
      copy.removeAttribute("data-avatar-builder-target")
      form.appendChild(copy)
    })

    form.requestSubmit()
  }

  #submitLook() {
    this.element.querySelector("#avatar-look-form")?.requestSubmit()
  }

  #trial() {
    return this.tileTargets.find((tile) => tile.dataset.locked && tile.dataset.active === "true")
  }

  #askToUnlock(trial) {
    const price = Number(trial.dataset.price)
    const affordable = price <= this.balanceValue
    const copy = this.confirmTemplateTarget.dataset
    const fill = (text) => text
      .replace(/%\{item\}/g, trial.dataset.name)
      .replace(/%\{price\}/g, price.toLocaleString())
      .replace(/%\{left\}/g, (this.balanceValue - price).toLocaleString())
      .replace(/%\{balance\}/g, this.balanceValue.toLocaleString())

    const modal = this.confirmTemplateTarget.content.cloneNode(true)
    const part = (name) => modal.querySelector(`[data-confirm="${name}"]`)

    part("header").querySelector("h2").textContent = fill(affordable ? copy.unlockTitle : copy.shortTitle)
    part("body").textContent = fill(affordable ? copy.unlockBody : copy.shortBody)
    part("cancel").textContent = fill(affordable ? copy.unlockCancel : copy.shortCancel)
    // A look they cannot afford yet has nothing to confirm, so the way out is simply
    // to close.
    if (affordable) {
      part("primary").textContent = fill(copy.unlockAction)
      part("primary").dataset.action = "click->avatar-builder#confirmUnlock"
    } else {
      part("primary").hidden = true
    }

    this.confirmHostTarget.replaceChildren(modal)
  }

  #revert(slot) {
    const owned = this.tileTargets.find((tile) =>
      tile.dataset.slot === slot && tile.dataset.variant === this.lastOwned[slot]
    )

    if (owned) this.#applyVariant(owned)
  }

  pickVariant(event) {
    this.#applyVariant(event.currentTarget)
    this.#record()
    this.#pop()
  }

  pickColour(event) {
    this.#applyColour(event.currentTarget)
    this.#record()
    this.#pop()
  }

  // Every slot rerolled at once — the fastest way to see what the kit can do.
  // Only from what they already own, so a reroll is never a look they cannot keep.
  randomise() {
    const owned = this.tileTargets.filter((tile) => !tile.dataset.locked)

    Object.values(this.#groupBySlot(owned)).forEach((tiles) => {
      this.#applyVariant(tiles[Math.floor(Math.random() * tiles.length)])
    })
    Object.values(this.#groupBySlot(this.swatchTargets)).forEach((swatches) => {
      this.#applyColour(swatches[Math.floor(Math.random() * swatches.length)])
    })
    this.#record()
    this.#pop()
  }

  // A step is the whole look, not the one slot that changed: restoring a slot at a
  // time would strand a colour on a variant that was never worn with it.
  #snapshot() {
    return {
      variants: { ...this.current },
      colours: Object.fromEntries(
        this.colourFieldTargets
          .filter((field) => !this.derived.has(field.dataset.slot))
          .map((field) => [field.dataset.slot, field.value])
      ),
    }
  }

  #record() {
    this.history = this.history.slice(0, this.cursor + 1)
    this.history.push(this.#snapshot())
    this.cursor = this.history.length - 1
    this.#changed()
  }

  #travel(step) {
    const next = this.cursor + step
    if (next < 0 || next > this.history.length - 1) return

    this.cursor = next
    this.#restore(this.history[next])
    this.#changed()
    this.#pop()
  }

  #restore({ variants, colours }) {
    Object.entries(variants).forEach(([slot, variant]) => {
      const tile = this.tileTargets.find((t) => t.dataset.slot === slot && t.dataset.variant === variant)
      if (tile) this.#applyVariant(tile)
    })

    // Eyebrows have no row of their own — the hair swatch paints them, so a missing
    // swatch here is expected rather than a gap to fill.
    Object.entries(colours).forEach(([slot, colour]) => {
      const swatch = this.swatchTargets.find((s) => s.dataset.slot === slot && s.dataset.colour === colour)
      if (swatch) this.#applyColour(swatch)
    })
  }

  #changed() {
    this.#refresh()
    this.#autosave()
  }

  #refresh() {
    this.#announceHistory()
    this.#offerUnlock()
  }

  // Every pick writes itself, so a student can wander off mid-change and keep it.
  // Debounced, or flicking through hairstyles would post once a frame. A locked item
  // rides along and the server drops it — only what they own can be worn.
  #autosave() {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null
      this.#submitLook()
    }, 500)
  }

  // Leaving the page inside the debounce would lose the last pick, and a normal
  // request does not survive the teardown — a beacon does.
  disconnect() {
    if (!this.saveTimer) return

    clearTimeout(this.saveTimer)
    const form = this.element.querySelector("#avatar-look-form")
    if (form) navigator.sendBeacon(form.action, new FormData(form))
  }

  // While a locked item is being worn on trial, the canvas carries the price as a
  // button. Save is for keeping a look; buying is its own, louder thing.
  #offerUnlock() {
    if (!this.hasUnlockTarget) return

    const trial = this.#trial()
    this.unlockTarget.hidden = !trial
    if (!trial) return

    this.unlockLabelTarget.textContent = this.unlockTarget.dataset.cta
      .replace("%{item}", trial.dataset.name)
      .replace("%{price}", Number(trial.dataset.price).toLocaleString())
  }

  // The undo and redo buttons live in the page header, outside this element, so
  // their state travels as an event rather than a reach across the DOM.
  #announceHistory() {
    this.dispatch("history", { detail: { canUndo: this.canUndo, canRedo: this.canRedo } })
  }

  // The SVG carries its own inline custom properties, so writing them on the
  // wrapper would lose to the element's own style. Always target the SVG itself.
  get canvases() {
    const found = [...this.canvasTarget.querySelectorAll("svg")]

    return found.length ? found : [this.canvasTarget]
  }

  #applyVariant(tile) {
    const { slot, variant, shade, locked } = tile.dataset

    // Only one thing is ever on trial: the preview should never promise two items
    // when the button can only buy one.
    if (locked) this.#endOtherTrials(slot)

    this.current[slot] = variant
    if (!locked) this.lastOwned[slot] = variant

    this.#show(slot, variant)
    // A hat replaces hair, so either slot changing re-decides whether hair shows.
    if (slot === "hair" || slot === "hat") this.#showHair()

    // Skin drives the nose and freckle shading, which live on other layers.
    if (shade) this.canvases.forEach((canvas) => canvas.style.setProperty("--wa-shade", shade))

    this.#field(this.fieldTargets, slot).value = variant
    this.#select(this.tileTargets, slot, tile)
  }

  #endOtherTrials(keep) {
    this.tileTargets
      .filter((tile) => tile.dataset.locked && tile.dataset.active === "true" && tile.dataset.slot !== keep)
      .forEach((tile) => this.#revert(tile.dataset.slot))
  }

  #show(slot, variant) {
    this.canvases.forEach((canvas) => {
      canvas.querySelectorAll(`g[data-slot="${slot}"]`).forEach((layer) => {
        layer.style.display = layer.dataset.variant === variant ? "" : "none"
      })
    })
  }

  #showHair() {
    const hatted = this.covering.has(this.current.hat)

    this.canvases.forEach((canvas) => {
      canvas.querySelectorAll('g[data-slot="hair"]').forEach((layer) => {
        const match = !hatted && layer.dataset.variant === this.current.hair
        layer.style.display = match ? "" : "none"
      })
    })
  }

  #applyColour(swatch) {
    const { slot, colour, also } = swatch.dataset

    // One row can own more than one slot — hair colour is eyebrow colour.
    const slots = [slot, ...(also ? also.split(" ") : [])]

    slots.forEach((name) => this.#paint(name, colour))
    this.#select(this.swatchTargets, slot, swatch)
  }

  #paint(slot, colour) {
    this.canvases.forEach((canvas) => canvas.style.setProperty(`--wa-${slot}`, colour))
    // Each tile draws its own SVG with its own inline properties, so a colour has to
    // be written onto every one of them or the previews keep the colour they loaded with.
    this.#tilesFor(slot).forEach((tile) => {
      tile.style.setProperty(`--wa-${slot}`, colour)
      tile.querySelector("svg")?.style.setProperty(`--wa-${slot}`, colour)
    })
    this.#field(this.colourFieldTargets, slot).value = colour
  }

  #tilesFor(slot) {
    return this.tileTargets.filter((tile) => tile.dataset.slot === slot)
  }

  // Restart the animation on every change, so rapid picking still reads as feedback.
  #pop() {
    const stage = this.canvasTarget
    stage.classList.remove("avatar-stage-pop")
    void stage.offsetWidth
    stage.classList.add("avatar-stage-pop")
  }

  #celebrate() {
    const from = this.balanceValue + this.spentValue
    const started = performance.now()
    const duration = 700

    const step = (now) => {
      const progress = Math.min((now - started) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      this.balanceTarget.textContent = Math.round(from - this.spentValue * eased).toLocaleString()
      if (progress < 1) requestAnimationFrame(step)
    }

    if (this.hasBalanceTarget) requestAnimationFrame(step)
  }

  #field(targets, slot) {
    return targets.find((target) => target.dataset.slot === slot) ?? {}
  }

  #groupBySlot(targets) {
    return targets.reduce((groups, target) => {
      ;(groups[target.dataset.slot] ||= []).push(target)
      return groups
    }, {})
  }

  #activeIn(targets) {
    return targets.filter((target) => target.dataset.active === "true")
  }

  #select(targets, slot, chosen) {
    targets
      .filter((target) => target.dataset.slot === slot)
      .forEach((target) => {
        target.dataset.active = String(target === chosen)
        target.setAttribute("aria-pressed", String(target === chosen))
      })
  }
}
